import type { SelectChangeEvent } from '@mui/material';
import {
  Autocomplete,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import type React from 'react';
import { ACTIVITY_TYPES } from '../../../services/api';
import type { ActivityFilters } from './types';

interface ActivityFiltersProps {
  filters: ActivityFilters;
  handleFilterChange: (field: keyof ActivityFilters, value: string) => void;
  handleSelectChange: (field: keyof ActivityFilters) => (event: SelectChangeEvent<string>) => void;
  usersList: { label: string; id: string }[];
}

export const ActivityFiltersComponent: React.FC<ActivityFiltersProps> = ({
  filters,
  handleFilterChange,
  handleSelectChange,
  usersList,
}) => {
  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Filters
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            fullWidth
            label="Start Date"
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            fullWidth
            label="End Date"
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Activity Type</InputLabel>
            <Select
              value={filters.activityType}
              onChange={handleSelectChange('activityType')}
              label="Activity Type"
            >
              <MenuItem value="">All Types</MenuItem>
              {Object.values(ACTIVITY_TYPES).map((type) => (
                <MenuItem key={type} value={type}>
                  {type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Autocomplete
            options={usersList}
            getOptionLabel={(option) => option.label}
            value={usersList.find((u) => u.id === filters.userId) || null}
            onChange={(_event, newValue) => handleFilterChange('userId', newValue?.id || '')}
            renderInput={(params) => {
              const { InputLabelProps, InputProps, inputProps, ...rest } = params;
              return (
                <TextField
                  {...rest}
                  label="Target User"
                  size="small"
                  slotProps={{
                    inputLabel: InputLabelProps,
                    input: InputProps,
                    htmlInput: inputProps,
                  }}
                />
              );
            }}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel>User Role</InputLabel>
            <Select
              value={filters.targetUserRole}
              onChange={handleSelectChange('targetUserRole')}
              label="User Role"
            >
              <MenuItem value="">All Roles</MenuItem>
              <MenuItem value="client_admin">Admin</MenuItem>
              <MenuItem value="manager">Manager</MenuItem>
              <MenuItem value="user">User</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Paper>
  );
};
